import { debounce } from "lodash";
import { addEventListener } from "../util/events";
import { ComponentCreator, SearchResultComponent } from "./interfaces";
import format from "../util/format";
import Title from "./components/title";
import FilterItemDecisions from "./components/filterItemDecisions";
import FilterItemMetaData from "./components/filterItemMetaData";
import Description from "./components/description";

interface SearchFilter {
    key: string;
    values: string[];
}

interface SearchData {
    pageNumber: number;
    order: string;
    filters: SearchFilter[];
    term?: string;
    rootPageId?: number;
}

interface SearchResult extends Record<string, unknown> {
    title: string;
    createdDateTime: Date;
    id: number;
    url: string;
    contentTypeAlias: string;
    score: number;
    order: string[];
    theme: string;
}

interface SearchResponse {
    results: SearchResult[];
    pagination: SearchPagination;
}

interface SearchPagination {
    totalResults: number;
    perPage: number;
    showingPageNumber: number;
    totalPages: number;
    showingFrom: number;
    showingTo: number;
    hasMore: boolean;
}

(() => {

    const container = document.getElementById("filter-page-container");
    if (!container) {
        return;
    }

    const form = document.getElementById("filter-page-form") as HTMLFormElement;
    if (!form) {
        return;
    }

    const nodeId = container.getAttribute("data-node-id");

    const text = {
        results: container.getAttribute("data-label-results"),
        noResults: container.getAttribute("data-label-no-results"),
    };
    const params = {
        order: container.getAttribute("data-param-order"),
        page: container.getAttribute("data-param-page"),
    };
    const elements = {
        range: document.getElementById("filter-page-range"),
        results: document.getElementById("filter-page-results"),
        loading: document.getElementById("filter-page-loading"),
        previous: document.getElementById("filter-page-prev-btn") as HTMLButtonElement,
        next: document.getElementById("filter-page-next-btn") as HTMLButtonElement,
        term: document.getElementById("filter-page-term-input") as HTMLInputElement,
        order: document.getElementById("filter-page-order") as HTMLSelectElement,
        orderByRelevance: document.getElementById("filter-page-order-relevance") as HTMLOptionElement,
    }

    const componentCreatorsByKey = [
        Title,
        FilterItemDecisions,
        FilterItemMetaData,
        Description
    ].reduce<Record<string, ComponentCreator<unknown>>>((result, item) => {
        result[item.key] = item.createElement;
        return result;
    }, {});

    let currentPage = getPageFromQuery();

    let latestQuery: string;
    let latestRequest: Promise<Response>;

    // Handle submission
    form.addEventListener("submit", e => {
        e.preventDefault();
        searchDeferred(1);
    });

    // Handle keyword change
    if (elements.term) {
        elements.term.addEventListener("input", debounce(() => {
            const setSelected = elements.term.value.trim().length !== 0;
            toggleRelevance(setSelected);
            searchDeferred(1);
        }, 500));
    }

    // Checkbox, radio, or select changed
    addEventListener(container, "change", () => {
        searchDeferred(1);
    }, "input.js-filter-input,select.js-filter-input");

    // Date changed
    addEventListener(container, "change", debounce((e: Event) => {
        if (!(e.target instanceof HTMLInputElement) ||
            isNaN(Date.parse(e.target.value))) {
            return;
        }
        searchDeferred(1);
    }, 500), "input.js-filter-date");

    // Reset related on select
    addEventListener(container, "change", e => {
        if (!(e.target instanceof HTMLInputElement)) {
            return;
        }
        const idsAsString = e.target.getAttribute("data-filter-reset-value");
        if (!idsAsString) {
            return;
        }
        idsAsString.split(",").forEach(id => {
            const target = document.getElementById(id);
            if (target instanceof HTMLInputElement) {
                target.value = "";
            }
        });
    }, "[data-filter-reset-value]");

    // Set related on selected
    addEventListener(container, "change", e => {
        if (!(e.target instanceof HTMLInputElement)) {
            return;
        }
        const id = e.target.getAttribute("data-filter-set-checkbox");
        if (!id) {
            return;
        }
        const target = document.getElementById(id);
        if (target instanceof HTMLInputElement) {
            target.checked = true;
        }
    }, "[data-filter-set-checkbox]");

    // Clear buttons
    addEventListener(container, "click", e => {
        e.preventDefault();
        if (!(e.target instanceof HTMLElement)) {
            return;
        }
        const group = e.target.closest(".js-filter-group");
        if (!group) {
            return;
        }
        const elements = group.querySelectorAll(`input[name],select[name]`);
        if (!elements.length) {
            return;
        }
        elements.forEach(resetElement);
        toggleRelevance();
        searchDeferred(1);
    }, "button.js-clear-filters-button");

    // Search on previous button press
    elements.previous.addEventListener("click", (e) => {
        e.preventDefault();
        searchDeferred(currentPage - 1);
    });

    // Search on next button press
    elements.next.addEventListener("click", (e) => {
        e.preventDefault();
        searchDeferred(currentPage + 1);
    });

    // Search on order change
    elements.order.addEventListener("change", (e) => {
        e.preventDefault();
        searchDeferred(1);
    });

    //On load set checked/selected
    setInitialState();

    //On load search
    searchDeferred(currentPage);

    function getPageFromQuery(): number {
        const searchParams = new URLSearchParams(window.location.search);
        const value = parseInt(searchParams.get(params.page));
        return isNaN(value) ? 1 : Math.max(value, 1);
    }

    function setInitialState(): void {
        let element;
        new URL(window.location.href).searchParams.forEach((value, key) => {
            element = form.querySelector(`input[type="search"][name="${key}"]`);
            if (element instanceof HTMLInputElement) {
                element.value = value;
                return;
            }
            element = form.querySelector(`input[type="checkbox"][name="${key}"][value="${value}"],input[type="radio"][name="${key}"][value="${value}"]`);
            if (element instanceof HTMLInputElement) {
                element.checked = true;
                return;
            }
            element = form.querySelector(`select[name="${key}"] option[value="${value}"]`);
            if (element instanceof HTMLOptionElement) {
                element.selected = true;
                return;
            }
            element = form.querySelector(`input[type="date"][name="${key}"]`);
            if (element instanceof HTMLInputElement) {
                element.value = value;
                const radio = form.querySelector(`input[name="date"][value=""]`);
                if (radio instanceof HTMLInputElement) {
                    radio.checked = true;
                }
            }
        });
    }

    function resetElement(element: Element): void {
        if (element instanceof HTMLInputElement) {
            switch (element.type) {
                case "checkbox":
                case "radio":
                    element.checked = false;
                    break;
                default:
                    element.value = "";
                    break;
            }
        }
        else if (element instanceof HTMLSelectElement) {
            element.selectedIndex = 0;
        }
    }

    function toggleRelevance(setSelected = false): void {
        const option = elements.orderByRelevance;
        if (!option || !elements.term) {
            return;
        }
        const show = setSelected || elements.term.value.trim().length !== 0;
        if (show) {
            option.classList.remove("hidden");
            if (setSelected) {
                elements.order.value = option.value;
            }
        }
        else {
            elements.order.selectedIndex = 0;
            option.classList.add("hidden");
        }
    }

    function searchDeferred(page: number): void {
        setTimeout(() => {
            search(page);
        });
    }

    async function search(page: number): Promise<void> {
        page = Math.max(page, 1);
        const formData = new FormData(form);
        const order = elements.order.value;
        const query = getFormAsQuery(formData, order, page);
        if (query === latestQuery) {
            return;
        }
        showLoading();
        latestQuery = query;
        setState(query);
        const filters = getFormAsFilters(formData);
        const body: SearchData = {
            filters: filters,
            pageNumber: page,
            order: order,
        };
        const term = formData.get("term");
        if (term) {
            body.term = term.toString();
        }
        if (nodeId) {
            body.rootPageId = parseInt(nodeId);
        }
        const request = latestRequest = fetch(`/api/search`, {
            method: "POST",
            body: JSON.stringify(body),
            headers: {
                "content-type": "application/json"
            }
        });
        let data: SearchResponse = null;
        try {
            const response = await request;
            data = await response.json();
        }
        catch {
            // Fall through
        }
        if (request !== latestRequest) {
            return;
        }
        currentPage = data?.pagination?.showingPageNumber || 1;
        renderInformation(data);
        renderResults(data);
        showLoaded(data);
    }

    function getFormAsQuery(data: FormData, order: string, page: number): string {
        const query = new URLSearchParams();
        for (const [key, value] of data.entries()) {
            if (value) {
                query.append(key, value.toString());
            }
        }
        if (page !== 1) {
            query.set(params.page, page.toString());
        }
        if (order !== "newest") {
            query.set(params.order, order);
        }
        return query.toString();
    }

    function getFormAsFilters(formData: FormData): SearchFilter[] {
        const items: Record<string, string[]> = {};
        for (const [key, value] of formData.entries()) {
            if (!value || key.toLowerCase() == "term") {
                continue;
            }
            const values = items[key] ?? (() => {
                const values: string[] = [];
                items[key] = values;
                return values;
            })();
            values.push(value.toString());
        }
        const filters = Object.entries(items).map<SearchFilter>(([key, values]) => ({ key, values }))
        return filters;
    }

    function setState(query: string): void {
        if (query?.length) {
            window.history.replaceState("", "", `?${query}`);
        }
        else {
            window.history.replaceState({}, "", window.location.pathname);
        }
    }

    function showLoading(): void {
        elements.previous.disabled = true;
        elements.next.disabled = true;
        elements.results.classList.add("hidden");
        elements.loading.classList.remove("hidden");
    }

    function showLoaded(data: SearchResponse): void {
        const hasPrevious = 1 < (data?.pagination?.showingPageNumber ?? 1);
        const hasNext = data?.pagination?.hasMore ?? false;
        elements.loading.classList.add("hidden");
        elements.results.classList.remove("hidden");
        elements.previous.classList.toggle("hidden", !hasPrevious);
        elements.previous.disabled = !hasPrevious;
        elements.next.classList.toggle("hidden", !hasNext);
        elements.next.disabled = !hasNext;
    }

    function renderInformation(data: SearchResponse): void {
        if (!data || data.pagination.totalResults == 0) {
            elements.range.innerHTML = "&nbsp;";
            return;
        }
        elements.range.innerText = format(text.results,
            Intl.NumberFormat().format(data.pagination.showingFrom),
            Intl.NumberFormat().format(data.pagination.showingTo),
            Intl.NumberFormat().format(data.pagination.totalResults));
    }

    function renderResults(data: SearchResponse): void {
        elements.results.innerHTML = "";
        if (data?.results?.length) {
            const ul = document.createElement('ul');
            ul.className = 'list-none';
            elements.results.append(ul);
            data.results.forEach(result => {
                ul.appendChild(createResultElement(result));
            });
        }
        else {
            const element = document.createElement("div");
            element.innerHTML = text.noResults;
            elements.results.appendChild(element);
        }
    }

    function createResultElement(result: SearchResult): HTMLLIElement {
        const link = document.createElement("a");
        link.className = "block group";
        link.href = result.url;
        result.order.forEach(key => {
            const target = result[key];
            if (target) {
                const component = getElement(key, target, result.theme);
                if (component) {
                    link.append(component);
                }
            }
        });
        const li = document.createElement('li');
        li.className = 'border-dotted border-b-2 border-b-light pb-3 mt-3';
        li.appendChild(link);
        return li;
    }

    function getElement<T>(key: string, value: T, theme?: string): SearchResultComponent {
        const method = componentCreatorsByKey[key];
        if (method) {
            const result = method(value, theme);
            return result;
        }
    }
})();
